import { useState } from 'react';
import { Box, Flex, Stack, Text } from '@mantine/core';
import { Dropzone, MIME_TYPES, MS_WORD_MIME_TYPE, PDF_MIME_TYPE } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { IconFile, IconUpload, IconX } from '@tabler/icons-react';
import { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { TemplateCreationData } from 'src/company/templates/types';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useUploadToStorage } from 'src/data-capture/hooks/file-uploads/useUploadToStorage';
import { useConvertToMarkdownMutation } from 'src/company/templates/hooks/useConvertToMarkdownMutation';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';

export default function UploadDocumentsStep(props: StepProps<TemplateCreationData>) {
  const { data, setData } = props;
  const [exampleTemplate, setExampleTemplate] = useState<File | null>(null);
  const [examplePopulated, setExamplePopulated] = useState<File | null>(null);
  const { uploadToStorage } = useUploadToStorage();
  const { convertToMarkdown } = useConvertToMarkdownMutation();

  const handleExampleTemplateUpload = (files: File[]) => {
    if (files.length > 0) {
      setExampleTemplate(files[0]);
    }
  };

  const handleExamplePopulatedUpload = (files: File[]) => {
    if (files.length > 0) {
      setExamplePopulated(files[0]);
    }
  };

  const canGoForward = !!(exampleTemplate && examplePopulated);

  const handleNext = async () => {
    if (!exampleTemplate || !examplePopulated) {
      return;
    }

    try {
      const { location: exampleTemplateLocation } = await uploadToStorage(exampleTemplate!);
      const { location: examplePopulatedLocation } = await uploadToStorage(examplePopulated!);

      const exampleTemplateMarkdownResponse = await convertToMarkdown(props.data.company.id, {
        location: exampleTemplateLocation,
      });
      const examplePopulatedMarkdownResponse = await convertToMarkdown(props.data.company.id, {
        location: examplePopulatedLocation,
        collectionToAttachImagesTo: exampleTemplateMarkdownResponse.extractedImagesCollection?.id,
      });

      const newData = {
        ...data,
        exampleTemplateFile: exampleTemplateMarkdownResponse.dataUploadFile,
        exampleTemplateMarkdown: exampleTemplateMarkdownResponse.markdown,
        examplePopulatedFile: examplePopulatedMarkdownResponse.dataUploadFile,
        examplePopulatedMarkdown: examplePopulatedMarkdownResponse.markdown,
        extractedImagesCollection: examplePopulatedMarkdownResponse.extractedImagesCollection,
      };
      setData(newData);

      props.nextStep(newData);
    } catch (error) {
      notifyAndLogError(
        'Failed to upload template files',
        'An error occurred while uploading your template documents. Please try again.',
        error,
        {
          exampleTemplateFileName: exampleTemplate?.name || 'Undefined',
          examplePopulatedFileName: examplePopulated?.name || 'Undefined',
        },
      );
    }
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Upload template documents"
      description="Upload both a blank template and a completed example. We'll use these to understand the structure and generate AI instructions for each section."
      continueText="Continue"
      canGoForward={canGoForward}
      hideProgress={true}
      nextStep={handleNext}
    >
      <Stack gap="md">
        <Section>
          <Flex direction="column" gap="xxxs" mb="sm">
            <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10">
              Example Template
            </Text>
            <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
              Upload the example template document that needs to be filled out
            </Text>
          </Flex>

          {exampleTemplate ? (
            <FileDisplay file={exampleTemplate} onRemove={() => setExampleTemplate(null)} />
          ) : (
            <TemplateDropzone onFilesUploaded={handleExampleTemplateUpload} />
          )}
        </Section>

        <Section>
          <Flex direction="column" gap="xxxs" mb="sm">
            <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10">
              Example Populated
            </Text>
            <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
              Upload a populated version of the same template with sample content
            </Text>
          </Flex>

          {examplePopulated ? (
            <FileDisplay file={examplePopulated} onRemove={() => setExamplePopulated(null)} />
          ) : (
            <TemplateDropzone onFilesUploaded={handleExamplePopulatedUpload} />
          )}
        </Section>
      </Stack>
    </DrawerFlowScreenLayout>
  );
}

type SettingsListProps = {
  children: React.ReactNode;
};

function Section({ children }: SettingsListProps) {
  return (
    <Flex direction="column" bg="gray.0" style={{ borderRadius: 12, overflowX: 'hidden' }}>
      <Flex
        direction="column"
        gap="xs"
        p="md"
        bd="1px solid var(--mantine-color-gray-1)"
        style={{ borderRadius: 12, overflowX: 'hidden' }}
        bg="white"
      >
        {children}
      </Flex>
    </Flex>
  );
}

type TemplateDropzoneProps = {
  onFilesUploaded: (files: File[]) => void;
};

function TemplateDropzone({ onFilesUploaded }: TemplateDropzoneProps) {
  return (
    <Dropzone
      onDrop={onFilesUploaded}
      onReject={fileRejections => {
        fileRejections.forEach(fileRejection => {
          notifications.show({
            title: `Failed to upload ${fileRejection.file.name}`,
            message: fileRejection.errors.map(e => e.message).join('\n'),
            color: 'red',
          });
        });
      }}
      maxSize={50 * 1024 * 1024} // 50MB
      maxFiles={1}
      accept={[
        ...PDF_MIME_TYPE,
        ...MS_WORD_MIME_TYPE,
        MIME_TYPES.doc,
        MIME_TYPES.docx,
        'text/markdown',
        'text/plain',
      ]}
      style={{
        border: '1px dashed var(--mantine-color-gray-4)',
        borderRadius: '12px',
        padding: '16px',
        minHeight: '80px',
        backgroundColor: 'var(--mantine-color-gray-0)',
      }}
    >
      <Flex align="center" justify="center" gap="xs" style={{ minHeight: '48px' }}>
        <IconUpload size={16} color="var(--mantine-color-gray-6)" />
        <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.6">
          Drop files here or click to browse
        </Text>
      </Flex>
      <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.5" ta="center" mt="xs">
        Supports .txt, .docx, .pdf, .md files
      </Text>
    </Dropzone>
  );
}

type FileDisplayProps = {
  file: File;
  onRemove: () => void;
};

function FileDisplay({ file, onRemove }: FileDisplayProps) {
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box
      p="sm"
      style={{
        borderRadius: '12px',
        backgroundColor: 'var(--mantine-color-gray-1)',
        border: '1px solid var(--mantine-color-gray-2)',
      }}
    >
      <Flex align="center" justify="space-between">
        <Flex gap="xs" align="center">
          <IconFile size={20} color="var(--mantine-color-gray-6)" />
          <Stack gap={0}>
            <Text fw={FONT_WEIGHTS.medium} size="sm" c="gray.10">
              {file.name}
            </Text>
            <Text fw={FONT_WEIGHTS.regular} size="xs" c="gray.6">
              {formatFileSize(file.size)}
            </Text>
          </Stack>
        </Flex>
        <Box onClick={onRemove} style={{ cursor: 'pointer', padding: '4px' }}>
          <IconX size={16} color="var(--mantine-color-gray-6)" />
        </Box>
      </Flex>
    </Box>
  );
}
